import { useCallback, useEffect, useMemo, useState } from 'react';
import AccountsTab from './settings/AccountsTab';
import InterfaceTab from './settings/InterfaceTab';
import DemoDataTab from './settings/DemoDataTab';
import LlmNodesTab from './settings/LlmNodesTab';
import TranscriptVisibilityCard from './settings/TranscriptVisibilityCard';
import InternalCallsCard from './settings/InternalCallsCard';
import BiometryCard from './settings/BiometryCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from './ui/command';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle
} from './ui/alert-dialog';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import {
  cleanupStorage,
  fetchEmployeesFromTelephony,
  fetchTelephonyEmployeeSources,
  restoreEmployeesFromHistory,
  setEmployeeAnalysisEnabled,
  updateEmployee,
  updateTimeZoneSetting
} from '../api';
import useEmployees from '../hooks/useEmployees';
import useTimeZone from '../hooks/useTimeZone';
import {
  applySuggestions,
  describeFetchError,
  describeFetchResult,
  normalizeSources,
  resolveFetchButton
} from './settings/telephonyEmployeeFetch';
import { CLEANUP_OPTIONS, CLEANUP_WARNING, describeCleanupResult } from './settings/storageCleanup';

const initialCleanupState = CLEANUP_OPTIONS.reduce((acc, option) => {
  acc[option.key] = false;
  return acc;
}, {});

const Settings = ({ user }) => {
  const { toast } = useToast();
  const { employees, refresh } = useEmployees();
  const { timeZone, setTimeZone, isLoadingTimeZone } = useTimeZone();
  const isAdmin = user?.role === 'admin';
  const [timeZoneDraft, setTimeZoneDraft] = useState(timeZone ?? 'UTC');
  const [timeZoneOptions, setTimeZoneOptions] = useState(['UTC']);
  const [isSavingTimeZone, setIsSavingTimeZone] = useState(false);
  const [editedNames, setEditedNames] = useState({});
  const [editedDepartments, setEditedDepartments] = useState({});
  const [openDepartmentId, setOpenDepartmentId] = useState(null);
  const [departmentSearch, setDepartmentSearch] = useState({});
  const [savingIds, setSavingIds] = useState(new Set());
  const [saveErrors, setSaveErrors] = useState({});
  const [isFetchingFromTelephony, setIsFetchingFromTelephony] = useState(false);
  // Какие телефонии умеют отдать список сотрудников — от этого зависят подпись и показ кнопки.
  const [telephonySources, setTelephonySources] = useState({ loaded: false, sources: null });
  const [isRestoringFromHistory, setIsRestoringFromHistory] = useState(false);
  const [isSavingAll, setIsSavingAll] = useState(false);
  // Оптимистичное значение тумблера «Анализ» до того, как приедет свежий список сотрудников.
  // Без него переключатель не двигался бы до конца перезагрузки списка.
  const [analysisOverrides, setAnalysisOverrides] = useState({});
  const [analysisPendingIds, setAnalysisPendingIds] = useState(new Set());
  const [cleanupState, setCleanupState] = useState(initialCleanupState);
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
  const [confirmationInput, setConfirmationInput] = useState('');
  const [isCleaning, setIsCleaning] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '') ?? '';

  const resolveAnalysisEnabled = useCallback(
    (employee) => analysisOverrides[employee.id] ?? employee.analysisEnabled ?? true,
    [analysisOverrides]
  );

  // PUT перезаписывает карточку целиком, поэтому analysisEnabled обязан быть в payload:
  // без него сохранение имени или отдела молча включило бы анализ обратно.
  const buildEmployeePayload = useCallback((employee, fullName, department) => ({
    externalId: employee.externalId,
    fullName,
    department: typeof department === 'string' && department.trim().length > 0
      ? department.trim()
      : null,
    position: employee.position,
    status: employee.status,
    avatarUrl: employee.avatarUrl,
    hiredAt: employee.hiredAt,
    firedAt: employee.firedAt,
    analysisEnabled: resolveAnalysisEnabled(employee)
  }), [resolveAnalysisEnabled]);

  // Как только сервер подтвердил значение свежим списком, оптимистичную запись убираем,
  // иначе она навсегда перекрыла бы изменения, сделанные из другой сессии.
  useEffect(() => {
    setAnalysisOverrides((previous) => {
      const next = { ...previous };
      let changed = false;
      employees.forEach((employee) => {
        if (employee.id in next && next[employee.id] === (employee.analysisEnabled ?? true)) {
          delete next[employee.id];
          changed = true;
        }
      });
      return changed ? next : previous;
    });
  }, [employees]);

  useEffect(() => {
    let cancelled = false;
    fetchTelephonyEmployeeSources()
      .then((raw) => {
        if (!cancelled) {
          setTelephonySources({ loaded: true, sources: normalizeSources(raw) });
        }
      })
      .catch(() => {
        // Сбой списка кнопку не прячет: сервер сам ответит, настроена ли хоть одна телефония.
        if (!cancelled) {
          setTelephonySources({ loaded: true, sources: null });
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const fetchButton = resolveFetchButton(telephonySources);

  const handleToggleAnalysis = useCallback(async (employee, enabled) => {
    setAnalysisOverrides((previous) => ({ ...previous, [employee.id]: enabled }));
    setAnalysisPendingIds((previous) => new Set(previous).add(employee.id));

    try {
      await setEmployeeAnalysisEnabled(employee.id, enabled);
      refresh();
      toast({
        title: enabled ? 'Анализ включён' : 'Анализ выключен',
        description: enabled
          ? 'Новые звонки сотрудника будут анализироваться.'
          : 'Новые звонки сотрудника будут получать задание со статусом «Пропущено».'
      });
    } catch (error) {
      setAnalysisOverrides((previous) => {
        const next = { ...previous };
        delete next[employee.id];
        return next;
      });
      toast({
        title: 'Не удалось изменить настройку анализа',
        description: error?.response?.data?.message ?? error?.message ?? 'Повторите попытку позже.',
        variant: 'destructive'
      });
    } finally {
      setAnalysisPendingIds((previous) => {
        const next = new Set(previous);
        next.delete(employee.id);
        return next;
      });
    }
  }, [refresh, toast]);

  const departmentOptions = useMemo(
    () => [...new Set(
      employees
        .map((employee) => employee.department?.trim())
        .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b, 'ru', { sensitivity: 'base' })),
    [employees]
  );

  const existingDepartments = useMemo(
    () => new Set(departmentOptions.map((department) => department.toLowerCase())),
    [departmentOptions]
  );

  const isAnySelected = useMemo(
    () => CLEANUP_OPTIONS.some((option) => cleanupState[option.key]),
    [cleanupState]
  );

  const handleToggleOption = (key) => {
    setCleanupState((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleStartCleanup = () => {
    if (!isAnySelected) {
      return;
    }
    setCleanupDialogOpen(true);
  };

  const handleDialogOpenChange = (open) => {
    if (!open && !isCleaning) {
      setConfirmationInput('');
    }
    setCleanupDialogOpen(open);
  };

  const handleCleanupConfirm = async () => {
    if (confirmationInput.trim() !== 'Очистить') {
      toast({
        title: 'Введите «Очистить»',
        description: 'Для подтверждения необходимо ввести слово без лишних символов.',
        variant: 'destructive'
      });
      return;
    }

    setIsCleaning(true);
    try {
      const payload = {
        confirmation: confirmationInput.trim(),
        ...cleanupState
      };
      const result = await cleanupStorage(payload);
      const summary = describeCleanupResult(result);

      toast({
        title: 'Очистка завершена',
        description: summary || 'Выбранные данные успешно удалены.'
      });
      setCleanupDialogOpen(false);
      setConfirmationInput('');
    } catch (error) {
      const description =
        error?.response?.data?.message ?? error?.message ?? 'Повторите попытку позже.';
      toast({
        title: 'Не удалось очистить хранилище',
        description,
        variant: 'destructive'
      });
    } finally {
      setIsCleaning(false);
    }
  };

  useEffect(() => {
    const supported = typeof Intl !== 'undefined' && Intl.supportedValuesOf
      ? Intl.supportedValuesOf('timeZone')
      : null;

    const options = (supported && supported.length > 0)
      ? supported
      : ['UTC', 'Europe/Moscow', 'Europe/London', 'America/New_York'];

    setTimeZoneOptions(options);
  }, []);

  useEffect(() => {
    setTimeZoneDraft(timeZone ?? 'UTC');
  }, [timeZone]);

  useEffect(() => {
    setEditedNames((previous) => {
      const next = { ...previous };
      employees.forEach((employee) => {
        if (!(employee.id in next)) {
          next[employee.id] = employee.fullName ?? '';
        }
      });
      return next;
    });
    setEditedDepartments({});
    setDepartmentSearch({});
    setOpenDepartmentId(null);
  }, [employees]);

  const handleSaveTimeZone = async () => {
    if (!timeZoneDraft) {
      return;
    }

    setIsSavingTimeZone(true);
    try {
      await updateTimeZoneSetting(timeZoneDraft);
      setTimeZone(timeZoneDraft);
      toast({
        title: 'Часовой пояс сохранен',
        description: `Выбранный часовой пояс: ${timeZoneDraft}`
      });
    } catch (error) {
      toast({
        title: 'Не удалось сохранить часовой пояс',
        description: error?.response?.data?.message ?? error?.message ?? 'Повторите попытку позже.',
        variant: 'destructive'
      });
    } finally {
      setIsSavingTimeZone(false);
    }
  };

  const handleSaveEmployee = useCallback(async (employee) => {
    const newName = editedNames[employee.id] ?? employee.fullName ?? '';
    const newDepartment = editedDepartments[employee.id] ?? employee.department ?? '';

    setSavingIds((previous) => new Set(previous).add(employee.id));
    setSaveErrors((previous) => ({ ...previous, [employee.id]: null }));

    try {
      await updateEmployee(employee.id, buildEmployeePayload(employee, newName, newDepartment));
      refresh();
      toast({
        title: 'Сотрудник обновлен',
        description: 'Изменения по сотруднику успешно сохранены.'
      });
    } catch (error) {
      setSaveErrors((previous) => ({ ...previous, [employee.id]: 'Ошибка сохранения' }));
      toast({
        title: 'Не удалось сохранить сотрудника',
        description: error?.response?.data?.message ?? error?.message ?? 'Повторите попытку позже.',
        variant: 'destructive'
      });
    } finally {
      setSavingIds((previous) => {
        const next = new Set(previous);
        next.delete(employee.id);
        return next;
      });
    }
  }, [buildEmployeePayload, editedDepartments, editedNames, refresh, toast]);

  // Подсказки только заполняют черновики имён: в базу их пишет «Сохранить», как и раньше у Mango.
  const handleFetchFromTelephony = useCallback(async () => {
    setIsFetchingFromTelephony(true);
    try {
      const result = await fetchEmployeesFromTelephony();
      setEditedNames((previous) => applySuggestions(previous, result?.suggestions));
      toast(describeFetchResult(result, telephonySources.sources));
    } catch (error) {
      toast({ ...describeFetchError(error, telephonySources.sources), variant: 'destructive' });
    } finally {
      setIsFetchingFromTelephony(false);
    }
  }, [telephonySources.sources, toast]);

  const handleRestoreFromHistory = useCallback(async () => {
    setIsRestoringFromHistory(true);
    try {
      const result = await restoreEmployeesFromHistory();
      toast({
        title: `Добавлено: ${result.created}, уже было: ${result.alreadyExisted}`
      });
      refresh();
    } catch {
      toast({
        title: 'Ошибка восстановления',
        variant: 'destructive'
      });
    } finally {
      setIsRestoringFromHistory(false);
    }
  }, [refresh, toast]);

  const handleSaveAll = useCallback(async () => {
    const employeesToSave = employees.filter((employee) => {
      const currentName = employee.fullName ?? '';
      const editedName = editedNames[employee.id] ?? currentName;
      const currentDepartment = employee.department ?? '';
      const editedDepartment = editedDepartments[employee.id] ?? currentDepartment;
      const hasNameChange = editedName !== currentName;
      const hasDepartmentChange = editedDepartment !== currentDepartment;
      return hasNameChange || hasDepartmentChange;
    });

    if (employeesToSave.length === 0) {
      toast({
        title: 'Нет изменений',
        description: 'Сохранение не требуется.'
      });
      return;
    }

    setIsSavingAll(true);
    setSaveErrors((previous) => {
      const next = { ...previous };
      employeesToSave.forEach((employee) => {
        next[employee.id] = null;
      });
      return next;
    });

    try {
      const results = await Promise.allSettled(
        employeesToSave.map((employee) => {
          const newName = editedNames[employee.id] ?? employee.fullName ?? '';
          const newDepartment = editedDepartments[employee.id] ?? employee.department ?? '';
          return updateEmployee(employee.id, buildEmployeePayload(employee, newName, newDepartment));
        })
      );

      const failedEmployees = [];
      let updatedCount = 0;

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          updatedCount += 1;
        } else {
          failedEmployees.push(employeesToSave[index]);
        }
      });

      if (failedEmployees.length > 0) {
        setSaveErrors((previous) => {
          const next = { ...previous };
          failedEmployees.forEach((employee) => {
            next[employee.id] = 'Ошибка сохранения';
          });
          return next;
        });
      }

      refresh();

      if (failedEmployees.length === 0) {
        toast({
          title: 'Сотрудники обновлены',
          description: `Успешно обновлено: ${updatedCount}.`
        });
        return;
      }

      const failedList = failedEmployees
        .map((employee) => employee.fullName || employee.externalId || employee.id)
        .join(', ');

      toast({
        title: 'Сохранение завершено с ошибками',
        description: `Обновлено: ${updatedCount}. Ошибки: ${failedList}`,
        variant: 'destructive'
      });
    } finally {
      setIsSavingAll(false);
    }
  }, [buildEmployeePayload, editedDepartments, editedNames, employees, refresh, toast]);

  const handleDepartmentSearchChange = useCallback((employeeId, value) => {
    setDepartmentSearch((previous) => ({
      ...previous,
      [employeeId]: value
    }));
  }, []);

  const handleDepartmentSelect = useCallback((employeeId, department) => {
    const normalized = typeof department === 'string' ? department.trim() : '';
    setEditedDepartments((previous) => ({
      ...previous,
      [employeeId]: normalized
    }));
    setDepartmentSearch((previous) => ({
      ...previous,
      [employeeId]: ''
    }));
    setOpenDepartmentId(null);
  }, []);

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight">Настройки</h1>
        <p className="text-muted-foreground">
          Управляйте справочниками и дополнительными параметрами платформы.
        </p>
      </div>

      <Tabs trackId="settings.tabs" defaultValue="general" className="space-y-4">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="general">Общие</TabsTrigger>
          <TabsTrigger value="employees">Сотрудники</TabsTrigger>
          {isAdmin && <TabsTrigger value="accounts">Учётные записи</TabsTrigger>}
          {isAdmin && <TabsTrigger value="interface">Интерфейс</TabsTrigger>}
          {isAdmin && <TabsTrigger value="llm-nodes">Узлы анализа</TabsTrigger>}
          {isAdmin && <TabsTrigger value="demo-data">Демо-данные</TabsTrigger>}
        </TabsList>

        <TabsContent value="general" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Часовой пояс</CardTitle>
              <CardDescription>
                Используется для отображения времени в графиках и отчетах.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="w-full md:max-w-md">
                <Select trackId="settings.timezone.select" value={timeZoneDraft} onValueChange={setTimeZoneDraft}>
                  <SelectTrigger className="w-full" disabled={isLoadingTimeZone}>
                    <SelectValue placeholder="Выберите часовой пояс" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeZoneOptions.map((zone) => (
                      <SelectItem key={zone} value={zone}>
                        {zone}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                data-ev="settings.timezone.save"
                onClick={handleSaveTimeZone}
                disabled={isSavingTimeZone || isLoadingTimeZone}
                className="md:shrink-0"
              >
                {isSavingTimeZone ? 'Сохраняем...' : 'Сохранить'}
              </Button>
            </CardContent>
          </Card>

          <TranscriptVisibilityCard user={user} />

          <InternalCallsCard user={user} />

          <BiometryCard user={user} />

          {isAdmin && (
            <Card>
              <CardHeader>
                <CardTitle>Очистка хранилища</CardTitle>
                <CardDescription>
                  Администрирование окружающей инфраструктуры. Используйте с осторожностью — данные
                  удаляются безвозвратно.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 md:grid-cols-2">
                  {CLEANUP_OPTIONS.map((option) => (
                    <label
                      key={option.key}
                      className="flex cursor-pointer items-start gap-3 rounded-md border p-3"
                    >
                      <Checkbox
                        data-ev="settings.storage_cleanup.option_toggle"
                        checked={cleanupState[option.key]}
                        onCheckedChange={() => handleToggleOption(option.key)}
                      />
                      <div>
                        <p className="text-sm font-medium">{option.label}</p>
                        <p className="text-xs text-muted-foreground">{option.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="flex flex-col gap-2 rounded-md border border-dashed p-3 text-sm text-muted-foreground md:flex-row md:items-center md:justify-between">
                  <span>Удаление нельзя отменить — убедитесь, что выбранные данные больше не нужны.</span>
                  <Button
                    data-ev="settings.storage_cleanup.start"
                    variant="destructive"
                    onClick={handleStartCleanup}
                    disabled={!isAnySelected}
                    className="whitespace-nowrap"
                  >
                    Очистить хранилище
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="text-xs text-muted-foreground">
            Адрес бекенда:{' '}
            <span className="break-all font-mono">
              {backendUrl || 'не задан'}
            </span>
          </div>
        </TabsContent>

        <TabsContent value="employees">
          <Card>
            <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <CardTitle>Управление сотрудниками</CardTitle>
                <CardDescription>
                  Отображаемые имена и отделы. Тумблер «Анализ» исключает звонки сотрудника
                  из обработки: задание создаётся сразу со статусом «Пропущено», и его можно
                  обработать вручную кнопкой «Переобработать» на вкладке «Задания».
                </CardDescription>
              </div>
              <div className="flex flex-wrap gap-2 lg:shrink-0 lg:flex-nowrap">
                <Button
                  data-ev="settings.employees.restore_from_history"
                  variant="outline"
                  size="sm"
                  onClick={handleRestoreFromHistory}
                  disabled={isRestoringFromHistory || isSavingAll || isFetchingFromTelephony}
                >
                  {isRestoringFromHistory ? 'Восстанавливаем...' : 'Восстановить из истории'}
                </Button>
                {fetchButton.visible && (
                  <Button
                    data-ev="settings.employees.fetch_from_telephony"
                    variant="outline"
                    size="sm"
                    onClick={handleFetchFromTelephony}
                    disabled={isFetchingFromTelephony || isRestoringFromHistory || isSavingAll}
                  >
                    {isFetchingFromTelephony ? 'Загружаем...' : fetchButton.label}
                  </Button>
                )}
                <Button
                  data-ev="settings.employees.save_all"
                  size="sm"
                  onClick={handleSaveAll}
                  disabled={isSavingAll || isFetchingFromTelephony || isRestoringFromHistory}
                >
                  {isSavingAll ? 'Сохранение...' : 'Сохранить'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Внешний ID</TableHead>
                    <TableHead>ID</TableHead>
                    <TableHead>Имя</TableHead>
                    <TableHead>Отдел</TableHead>
                    <TableHead>Анализ</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground">
                        Нет сотрудников
                      </TableCell>
                    </TableRow>
                  )}
                  {employees.map((employee) => {
                    const selectedDepartment = editedDepartments[employee.id] ?? employee.department ?? '';
                    const searchValue = departmentSearch[employee.id] ?? '';
                    const trimmedSearch = searchValue.trim();
                    const canCreateDepartment =
                      trimmedSearch.length > 0 && !existingDepartments.has(trimmedSearch.toLowerCase());

                    return (
                    <TableRow key={employee.id}>
                      <TableCell className="text-xs font-mono text-muted-foreground">
                        {employee.externalId ?? '—'}
                      </TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground">
                        {employee.id}
                      </TableCell>
                      <TableCell>
                        <Input
                          data-ev="settings.employees.name_edit"
                          value={editedNames[employee.id] ?? employee.fullName ?? ''}
                          onChange={(event) =>
                            setEditedNames((previous) => ({
                              ...previous,
                              [employee.id]: event.target.value
                            }))
                          }
                          className="h-8 min-w-[160px]"
                          disabled={savingIds.has(employee.id) || isSavingAll}
                        />
                        {saveErrors[employee.id] && (
                          <p className="mt-1 text-xs text-red-600">{saveErrors[employee.id]}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Popover
                          open={openDepartmentId === employee.id}
                          onOpenChange={(open) => {
                            setOpenDepartmentId(open ? employee.id : null);
                            if (!open) {
                              setDepartmentSearch((previous) => ({
                                ...previous,
                                [employee.id]: ''
                              }));
                            }
                          }}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              data-ev="settings.employees.department_open"
                              variant="outline"
                              size="sm"
                              className="h-8 w-full justify-between max-lg:min-w-[140px]"
                              disabled={savingIds.has(employee.id) || isSavingAll}
                            >
                              <span className="truncate">{selectedDepartment || '—'}</span>
                              <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-0" align="start">
                            <Command shouldFilter={true}>
                              <CommandInput
                                placeholder="Отдел..."
                                value={searchValue}
                                onValueChange={(value) => handleDepartmentSearchChange(employee.id, value)}
                              />
                              <CommandList>
                                <CommandEmpty>Не найдено</CommandEmpty>
                                {canCreateDepartment && (
                                  <CommandGroup heading="Создать">
                                    <CommandItem
                                      trackId="settings.employees.department_create"
                                      value={`create-${trimmedSearch}`}
                                      onSelect={() => handleDepartmentSelect(employee.id, trimmedSearch)}
                                      className="cursor-pointer"
                                    >
                                      Создать «{trimmedSearch}»
                                    </CommandItem>
                                  </CommandGroup>
                                )}
                                <CommandGroup>
                                  {departmentOptions.map((department) => (
                                    <CommandItem
                                      trackId="settings.employees.department_select"
                                      key={department}
                                      value={department}
                                      onSelect={() => handleDepartmentSelect(employee.id, department)}
                                      className="cursor-pointer"
                                    >
                                      <Check className={`h-4 w-4 ${selectedDepartment === department ? 'opacity-100' : 'opacity-0'}`} />
                                      {department}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            data-ev="settings.employees.analysis_toggle"
                            checked={resolveAnalysisEnabled(employee)}
                            onCheckedChange={(checked) => handleToggleAnalysis(employee, checked)}
                            disabled={!isAdmin || analysisPendingIds.has(employee.id) || isSavingAll}
                            aria-label="Анализ звонков сотрудника"
                          />
                          {!isAdmin && (
                            <span className="text-xs text-muted-foreground">админ</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{employee.status ?? '—'}</TableCell>
                      <TableCell>
                        <Button
                          data-ev="settings.employees.save"
                          size="sm"
                          disabled={savingIds.has(employee.id) || isSavingAll}
                          onClick={() => handleSaveEmployee(employee)}
                        >
                          {savingIds.has(employee.id) ? 'Сохранение...' : 'Сохранить'}
                        </Button>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="interface">
            <InterfaceTab />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="llm-nodes">
            <LlmNodesTab />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="demo-data">
            <DemoDataTab />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="accounts">
            <AccountsTab />
          </TabsContent>
        )}
      </Tabs>

      <AlertDialog open={cleanupDialogOpen} onOpenChange={handleDialogOpenChange}>
        <AlertDialogContent trackId="settings.storage_cleanup.confirm_dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Подтверждение очистки</AlertDialogTitle>
            <AlertDialogDescription>
              {CLEANUP_WARNING} Чтобы подтвердить, введите слово «Очистить».
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 pt-2">
            <Input
              data-ev="settings.storage_cleanup.confirm_input"
              autoFocus
              value={confirmationInput}
              onChange={(event) => setConfirmationInput(event.target.value)}
              placeholder="Очистить"
              disabled={isCleaning}
            />
            <p className="text-xs text-muted-foreground">
              Учитываются только русские буквы без лишних пробелов.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              data-ev="settings.storage_cleanup.cancel"
              variant="outline"
              onClick={() => handleDialogOpenChange(false)}
              disabled={isCleaning}
            >
              Отмена
            </Button>
            <Button
              data-ev="settings.storage_cleanup.confirm"
              variant="destructive"
              onClick={handleCleanupConfirm}
              disabled={confirmationInput.trim() !== 'Очистить' || isCleaning}
            >
              {isCleaning ? 'Очищаем...' : 'Удалить данные'}
            </Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Settings;
